// FacilitatorMcqEditor
// Question-set composition editor. Sets are pure compositions of
// Question-library items — no inline authoring here. Questions are
// added/removed via the inline "Add questions from library" panel; to
// edit a question's content, the lecturer opens it in the library.
//
// Legacy templates from older sets (no sourceRef) still render as a
// locked row showing the prompt + a Remove button. The lecturer can
// drop the legacy question and re-add the equivalent from the library
// without touching question content here.

import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import {
  emptyMcqExercise,
  normaliseMcqExercise,
  validateMcqExercise,
  MCQ_DIFFICULTIES,
  DIFFICULTY_LABELS,
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_TYPE_ICONS,
} from '../../lib/mcq';
import { QBANK_ITEM_TYPE_LABELS, QBANK_ITEM_TYPE_ICONS } from '../../lib/questionBankItems';
import { api } from '../../lib/api';
import EditorHelpPanel from './EditorHelpPanel';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const newTemplateId = () => `tpl-${Math.random().toString(36).slice(2, 10)}`;

const FacilitatorMcqEditor = ({ initialExercise, canCurateFreeTier, token, onSave, onPublish, onArchive, onDelete, onBack }) => {
  const [exercise, setExercise] = useState(() => normaliseMcqExercise(initialExercise || emptyMcqExercise()));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Inline picker panel — replaces the modal. Collapsed by default; the
  // educator clicks "Add questions" to expand it under the templates list.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerTypeFilter, setPickerTypeFilter] = useState('all');
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState(new Set());

  useEffect(() => {
    if (initialExercise) setExercise(normaliseMcqExercise(initialExercise));
  }, [initialExercise]);

  const issues = useMemo(() => validateMcqExercise(exercise), [exercise]);

  const updateExercise = (patch) => setExercise((current) => ({ ...current, ...patch }));
  const setTemplate = (index, next) => setExercise((current) => ({
    ...current,
    templates: current.templates.map((t, i) => (i === index ? next : t)),
  }));
  const removeTemplate = (index) => setExercise((current) => ({
    ...current,
    templates: current.templates.filter((_, i) => i !== index),
  }));
  const moveTemplate = (index, dir) => setExercise((current) => {
    const list = current.templates.slice();
    const j = index + dir;
    if (j < 0 || j >= list.length) return current;
    [list[index], list[j]] = [list[j], list[index]];
    return { ...current, templates: list };
  });

  // ---------- Inline library picker ----------------------------------------
  const togglePicker = async () => {
    setPickerSelected(new Set());
    setPickerOpen((open) => !open);
    if (pickerOpen || pickerItems.length || !token) return;
    setPickerLoading(true);
    try {
      const result = await api.listQuestionBankItems(token, { status: 'published' });
      setPickerItems(Array.isArray(result.items) ? result.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load the question library.');
    } finally {
      setPickerLoading(false);
    }
  };
  const togglePickerSelected = (id) => setPickerSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const addPicked = () => {
    const picked = pickerItems.filter((item) => pickerSelected.has(item.id));
    if (!picked.length) return;
    // Skip items already in the set (matched by their qbi sourceRef).
    const presentRefs = new Set(
      exercise.templates
        .filter((t) => typeof t.sourceRef === 'string' && t.sourceRef.startsWith('qbi:'))
        .map((t) => t.sourceRef.slice(4))
    );
    const fresh = picked.filter((item) => !presentRefs.has(item.id));
    if (!fresh.length) {
      setMessage('Selected questions are already in this set.');
      setTimeout(() => setMessage(''), 2000);
      setPickerSelected(new Set());
      return;
    }
    const newTemplates = fresh.map((item) => ({
      ...item.payload,
      id: newTemplateId(),
      templateType: item.templateType,
      sourceRef: `qbi:${item.id}`,
      sourceLabel: item.promptExcerpt || item.category || 'Library item',
    }));
    setExercise((current) => ({ ...current, templates: [...current.templates, ...newTemplates] }));
    setPickerSelected(new Set());
    setMessage(`Added ${fresh.length} question${fresh.length === 1 ? '' : 's'}.`);
    setTimeout(() => setMessage(''), 2000);
  };
  const filteredPickerItems = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return pickerItems.filter((item) => {
      if (pickerTypeFilter !== 'all' && item.templateType !== pickerTypeFilter) return false;
      if (!q) return true;
      const hay = `${item.promptExcerpt || ''} ${item.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pickerItems, pickerTypeFilter, pickerSearch]);
  const pickerTypesPresent = useMemo(
    () => Array.from(new Set(pickerItems.map((item) => item.templateType))),
    [pickerItems]
  );
  const presentRefIds = useMemo(() => new Set(
    exercise.templates
      .filter((t) => typeof t.sourceRef === 'string' && t.sourceRef.startsWith('qbi:'))
      .map((t) => t.sourceRef.slice(4))
  ), [exercise.templates]);

  // ---------- Save ---------------------------------------------------------
  const handleSave = async ({ publish = false } = {}) => {
    if (!onSave) return;
    setSaving(true); setError(''); setMessage('');
    try {
      const saved = await onSave({
        title: exercise.title,
        summary: exercise.summary,
        freeTierEligible: exercise.freeTierEligible,
        category: exercise.category,
        difficulty: exercise.difficulty,
        isCuratedRevisionSet: Boolean(exercise.isCuratedRevisionSet),
        payload: {
          mode: exercise.mode,
          settings: exercise.settings,
          templates: exercise.templates,
        },
      });
      if (publish && onPublish) {
        const id = saved?.id || exercise.id;
        if (id) await onPublish(id);
        setMessage('Saved and published.');
      } else {
        setMessage('Saved.');
      }
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setError(err?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return (
    <div className="student-page drill-editor-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title={exercise.id ? 'Edit question set' : 'Create question set'}
          description="Pick questions from the library, give the set a title, choose how it's played. Edit question content in the library itself."
          onBack={onBack}
          backLabel="Back to library"
          actions={<>
            <Button type="button" variant="primary" size="sm"
              onClick={() => handleSave({ publish: false })}
              disabled={saving}>
              {saving ? 'Saving…' : (exercise.status === 'published' ? 'Save changes' : 'Save draft')}
            </Button>
            {exercise.status !== 'published' && onPublish ? (
              <Button type="button" variant="success" size="sm"
                onClick={() => handleSave({ publish: true })}
                disabled={saving || issues.length > 0}>
                {exercise.id ? 'Publish' : 'Save and publish'}
              </Button>
            ) : null}
            {exercise.id && exercise.status === 'published' && onArchive ? (
              <Button type="button" variant="outline-dark" size="sm" onClick={() => onArchive(exercise.id)}>Archive</Button>
            ) : null}
            {exercise.id && onDelete ? (
              <Button type="button" variant="outline-danger" size="sm" onClick={() => onDelete(exercise.id)}>Delete</Button>
            ) : null}
          </>}
        />

        {message ? <Alert variant="success">{message}</Alert> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {issues.length ? (
          <Alert variant="warning">
            <strong>Fix before publishing:</strong>
            <ul className="mb-0">{issues.map((i) => <li key={i.key}>{i.message}</li>)}</ul>
          </Alert>
        ) : null}

        <EditorHelpPanel title="How sets work" defaultOpen={!exercise.id}>
          <p>A set is a named bundle of questions students can run end-to-end.</p>
          <ul>
            <li>Add questions by picking them from the Question library — the same question can sit in many sets without duplication.</li>
            <li><strong>Curated revision set</strong>: students see this set in the Mixed-revision picker. Per-question, the "Available in random pool" switch controls whether it can also be drawn into a random revision run.</li>
            <li>To edit a question's content, open it in the Question library. Changes there don't rewrite this set — pick the updated version again if you want it refreshed.</li>
          </ul>
        </EditorHelpPanel>

        <Card className="drill-editor-card mb-3">
          <Card.Body>
            <Row className="g-3">
              <Form.Group as={Col} md={6}>
                <Form.Label>Set title</Form.Label>
                <Form.Control value={exercise.title} onChange={(e) => updateExercise({ title: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md={6}>
                <Form.Label>Summary (one line shown in catalogue)</Form.Label>
                <Form.Control value={exercise.summary} onChange={(e) => updateExercise({ summary: e.target.value })} />
              </Form.Group>
            </Row>
            <Row className="g-3 mt-1">
              <Form.Group as={Col} md={6}>
                <Form.Label>Category (topic tag)</Form.Label>
                <Form.Control
                  value={exercise.category}
                  onChange={(e) => updateExercise({ category: e.target.value })}
                  placeholder="e.g. Antimicrobials, Anticoagulation, Pain"
                  maxLength={80}
                />
              </Form.Group>
              <Form.Group as={Col} md={6}>
                <Form.Label>Difficulty</Form.Label>
                <Form.Select value={exercise.difficulty} onChange={(e) => updateExercise({ difficulty: e.target.value })}>
                  <option value="">— Not set —</option>
                  {MCQ_DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>{DIFFICULTY_LABELS[d]}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>
            <Row className="g-3 mt-1">
              <Form.Group as={Col} md={4}>
                <Form.Label>Questions per session</Form.Label>
                <Form.Control type="number" min={1} value={exercise.settings.questionCount}
                  onChange={(e) => updateExercise({ settings: { ...exercise.settings, questionCount: Math.max(1, Number(e.target.value) || 1) } })} />
                <Form.Text className="text-muted">Capped at the number of questions in the set.</Form.Text>
              </Form.Group>
              <Form.Group as={Col} md={4} className="d-flex align-items-end">
                <Form.Check type="switch" id="mcq-shuffle" label="Shuffle question order each session"
                  checked={exercise.settings.shuffle}
                  onChange={(e) => updateExercise({ settings: { ...exercise.settings, shuffle: e.target.checked } })} />
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>Player mode</Form.Label>
                <Form.Select value={exercise.mode === 'challenge' ? 'challenge' : 'learn'}
                  onChange={(e) => updateExercise({ mode: e.target.value })}>
                  <option value="learn">Learn — reflective, shows the teaching note</option>
                  <option value="challenge">Challenge — gamified, running score</option>
                </Form.Select>
              </Form.Group>
            </Row>
            <Row className="g-3 mt-1">
              <Form.Group as={Col} md={canCurateFreeTier ? 6 : 12}>
                <Form.Check type="switch" id="mcq-curated-revision"
                  label="Show in Mixed-revision picker as a curated set"
                  checked={Boolean(exercise.isCuratedRevisionSet)}
                  onChange={(e) => updateExercise({ isCuratedRevisionSet: e.target.checked })} />
                <Form.Text className="text-muted">
                  When on, students see this set listed in their Mixed-revision picker. The per-question <strong>Available in random pool</strong> switch below controls which questions can also be drawn into random runs.
                </Form.Text>
              </Form.Group>
              {canCurateFreeTier ? (
                <Form.Group as={Col} md={6}>
                  <Form.Check type="switch" id="mcq-free-tier" label="Free tier (visible to non-paying students)"
                    checked={exercise.freeTierEligible}
                    onChange={(e) => updateExercise({ freeTierEligible: e.target.checked })} />
                </Form.Group>
              ) : null}
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between align-items-center mt-3 mb-2 flex-wrap gap-2">
          <h4 className="mb-0">Questions <Badge bg="light" text="dark">{exercise.templates.length}</Badge></h4>
          {token ? (
            <Button type="button" variant={pickerOpen ? 'secondary' : 'primary'} onClick={togglePicker}>
              <i className={`bi ${pickerOpen ? 'bi-chevron-up' : 'bi-plus-lg'} me-1`} />
              {pickerOpen ? 'Hide library picker' : 'Add questions from library'}
            </Button>
          ) : null}
        </div>

        {exercise.templates.length === 0 ? (
          <Alert variant="light" className="border text-center">
            <h6 className="mb-1"><i className="bi bi-stack me-2" />No questions in this set yet</h6>
            <p className="text-muted small mb-0">Click <strong>Add questions from library</strong> to pick from your published items.</p>
          </Alert>
        ) : (
          <div className="d-flex flex-column gap-2">
            {exercise.templates.map((template, index) => {
              const templateType = template.templateType || 'mcq';
              const isImported = typeof template.sourceRef === 'string' && template.sourceRef.startsWith('qbi:');
              const isLegacy = !isImported;
              const typeLabel = QBANK_ITEM_TYPE_LABELS[templateType] || TEMPLATE_TYPE_LABELS[templateType] || templateType;
              const typeIcon = QBANK_ITEM_TYPE_ICONS[templateType] || TEMPLATE_TYPE_ICONS[templateType] || 'bi-question-square';
              const prompt = template.prompt || template.sourceLabel || '(no prompt)';
              return (
                <Card key={template.id || index} className={isLegacy ? 'border-warning-subtle' : ''}>
                  <Card.Body className="py-3">
                    <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <span className="text-muted small">{index + 1}.</span>
                          <Badge bg="info">
                            <i className={`bi ${typeIcon} me-1`} aria-hidden="true" />
                            {typeLabel}
                          </Badge>
                          {isImported ? (
                            <Badge bg="light" text="dark">
                              <i className="bi bi-link-45deg me-1" />From library
                            </Badge>
                          ) : (
                            <Badge bg="warning" text="dark">
                              <i className="bi bi-exclamation-triangle me-1" />Legacy
                            </Badge>
                          )}
                        </div>
                        <div className="fw-semibold">{prompt}</div>
                        {isLegacy ? (
                          <div className="small text-muted mt-1">
                            This question was authored inline before the library existed. To edit it, recreate it as a library item, then add it back here and remove this one.
                          </div>
                        ) : null}
                      </div>
                      <div className="d-flex gap-2 align-items-center flex-wrap">
                        {exercise.isCuratedRevisionSet ? (
                          <Form.Check type="switch" id={`mcq-${index}-random`}
                            label="Available in random pool"
                            checked={template.availableForRandom !== false}
                            onChange={(e) => setTemplate(index, { ...template, availableForRandom: e.target.checked })} />
                        ) : null}
                        <div className="btn-group">
                          <Button size="sm" variant="outline-secondary"
                            onClick={() => moveTemplate(index, -1)} disabled={index === 0}
                            title="Move up">
                            <i className="bi bi-arrow-up" />
                          </Button>
                          <Button size="sm" variant="outline-secondary"
                            onClick={() => moveTemplate(index, 1)} disabled={index === exercise.templates.length - 1}
                            title="Move down">
                            <i className="bi bi-arrow-down" />
                          </Button>
                        </div>
                        <Button size="sm" variant="outline-danger" onClick={() => removeTemplate(index)}>
                          <i className="bi bi-x-lg" /> Remove
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}

        {/* Inline library picker — collapsible panel below the templates */}
        {pickerOpen ? (
          <Card className="mt-3 border-primary-subtle">
            <Card.Body>
              <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
                <InputGroup style={{ maxWidth: '24rem' }}>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search prompt or topic…"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    autoFocus
                  />
                  {pickerSearch ? (
                    <Button variant="outline-secondary" onClick={() => setPickerSearch('')}>Clear</Button>
                  ) : null}
                </InputGroup>
                <div className="d-flex gap-2 flex-wrap">
                  <Button size="sm" variant={pickerTypeFilter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setPickerTypeFilter('all')}>
                    All ({pickerItems.length})
                  </Button>
                  {pickerTypesPresent.map((type) => (
                    <Button
                      key={type}
                      size="sm"
                      variant={pickerTypeFilter === type ? 'primary' : 'outline-primary'}
                      onClick={() => setPickerTypeFilter(type)}
                    >
                      <i className={`bi ${QBANK_ITEM_TYPE_ICONS[type] || 'bi-question-square'} me-1`} aria-hidden="true" />
                      {QBANK_ITEM_TYPE_LABELS[type] || type}
                    </Button>
                  ))}
                </div>
                <div className="ms-auto">
                  <Button variant="primary" disabled={pickerSelected.size === 0} onClick={addPicked}>
                    Add {pickerSelected.size > 0 ? `${pickerSelected.size} ` : ''}question{pickerSelected.size === 1 ? '' : 's'}
                  </Button>
                </div>
              </div>
              {pickerLoading ? (
                <div className="text-muted text-center p-4">Loading library…</div>
              ) : pickerItems.length === 0 ? (
                <div className="text-muted text-center p-4 border rounded bg-light">
                  No published questions in the library yet. Author some in <strong>Question library → Questions</strong> first.
                </div>
              ) : filteredPickerItems.length === 0 ? (
                <div className="text-muted text-center p-4 border rounded bg-light">
                  No matching questions.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2" style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                  {filteredPickerItems.map((item) => {
                    const checked = pickerSelected.has(item.id);
                    const alreadyInSet = presentRefIds.has(item.id);
                    return (
                      <Card
                        key={item.id}
                        className={`${checked ? 'border-primary' : ''} ${alreadyInSet ? 'opacity-50' : ''}`}
                        role={alreadyInSet ? undefined : 'button'}
                        onClick={() => { if (!alreadyInSet) togglePickerSelected(item.id); }}
                      >
                        <Card.Body className="py-2">
                          <div className="d-flex align-items-start gap-3">
                            <Form.Check
                              type="checkbox"
                              checked={checked}
                              disabled={alreadyInSet}
                              onChange={() => togglePickerSelected(item.id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select question ${item.id}`}
                            />
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <Badge bg="info">
                                  <i className={`bi ${QBANK_ITEM_TYPE_ICONS[item.templateType] || 'bi-question-square'} me-1`} aria-hidden="true" />
                                  {QBANK_ITEM_TYPE_LABELS[item.templateType] || item.templateType}
                                </Badge>
                                {item.category ? <Badge bg="light" text="dark"><i className="bi bi-tag me-1" />{item.category}</Badge> : null}
                                {item.difficulty ? <Badge bg="secondary">{item.difficulty}</Badge> : null}
                                {alreadyInSet ? <Badge bg="success">In set</Badge> : null}
                              </div>
                              <div>{item.promptExcerpt || <em className="text-muted">(no prompt)</em>}</div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        ) : null}
      </Container>
    </div>
  );
};

export default FacilitatorMcqEditor;
